<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      label: string;
      name: string;
      options: Array<Schemas['PropertyGroupOption']>;
      entities: Array<Schemas['ProductManufacturer']>;
    }
  "
>
import { inject, ref } from "vue";
import { getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";

const props = defineProps<{
  filter: ListingFilter;
}>();

const emits = defineEmits<{
  (e: "select-value", value: { code: string; value: unknown }): void;
}>();
const selectedOptionIds = inject<string[]>("selectedOptionIds") as ComputedRef<
  string[]
>;
const isFilterVisible = ref<boolean>(false);
const toggle = () => {
  isFilterVisible.value = !isFilterVisible.value;
};
if (
  props.filter.options?.some((option) =>
    selectedOptionIds.value.includes(option.id)
  ) ||
  props.filter.entities?.some((entity) =>
    selectedOptionIds.value.includes(entity.id)
  )
) {
  toggle();
}
</script>

<template>
  <div>
    <button
      class="accordion-header w-full text-left py-1 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggle"
    >
      <span class="text-base font-bold text-dark-bg">{{
        props.filter.label
      }}</span>
      <svg
        id="icon-price"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[16px] w-[16px] transform transition-transform duration-200"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <transition name="fade" mode="out-in">
      <div :class="[!isFilterVisible ? 'hidden' : 'pt-4']">
        <ul
          class="flex flex-col gap-y-3.5 prose-li:relative prose-li:flex prose-li:gap-x-2.5 [&>li>label]:py-0.5 [&>li>label]:flex-1 [&>li>input]:opacity-0 [&>li>input]:cursor-pointer [&>li>input]:absolute [&>li>input]:w-full [&>li>input]:left-0 [&>li>span]:w-5 [&>li>span]:h-5 [&>li>span]:inline-block [&>li>span]:text-[0] [&>li>input:checked+span]:text-sm [&>li>span]:border [&>li>span]:rounded"
          :id="props.filter.code"
        >
          <li
            class="mb-2 text-sm font-normal"
            v-for="option in props.filter.options || props.filter.entities"
            :key="`${option.id}-${selectedOptionIds?.includes(option.id)}`"
          >
            <div class="flex items-center justify-start">
              <label
                class="relative flex cursor-pointer items-center"
                :for="`filter-mobile-${props.filter.code}-${option.id}`"
                data-ripple-dark="true"
              >
                <input
                  :id="`filter-mobile-${props.filter.code}-${option.id}`"
                  :checked="selectedOptionIds?.includes(option.id)"
                  :name="props.filter.name"
                  :value="option.name"
                  :aria-label="`${option.name} filter`"
                  type="checkbox"
                  @change="
                    emits('select-value', {
                      code: props.filter.code,
                      value: option.id,
                    })
                  "
                  class="peer relative h-4 w-4 cursor-pointer appearance-none border border-custom-grey transition-all checked:border-dark-bg checked:bg-trnsparent checked:before:bg-slate-400"
                />
              </label>

              <div v-if="option.media?.url">
                <NuxtImg
                  loading="lazy"
                  class="ml-2 h-4 w-4"
                  :src="option.media.url"
                  :alt="option.media.translated.alt || ''"
                  :class="{
                    'border-blue border-2': selectedOptionIds?.includes(
                      option.id
                    ),
                  }"
                />
              </div>
              <div
                v-else-if="option.colorHexCode"
                class="ml-2 h-4 w-4"
                :style="`background-color: ${option.colorHexCode}`"
                :class="{
                  'border-blue border-2': selectedOptionIds?.includes(
                    option.id
                  ),
                }"
              />
              <label
                :for="`filter-mobile-${props.filter.code}-${option.id}`"
                class="cursor-pointer text-slate-600 text-sm ml-3 truncate max-w-[130px]"
              >
                {{ getTranslatedProperty(option, "name") }}
              </label>
            </div>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
